import { useAdminAccess } from "src/hooks/useAdminAccess";
import { useEffect, useRef, useState } from "react";
import InlineSVG from "react-inlinesvg";
import { WithReset } from "src/sudokit/Layout/BoxReset";
import { getStoryBibleLedgerContextLabel, mergeEntityImageBatchEntries, mergeRambleEntries } from "~shared/LedgerEntryDisplayMap";
import { getLedgerDisplayInfo } from "../../models/LedgerEntriesDisplay";
import { useToast } from "../ToastWrapper";
import { StyledEntryContainer, StyledLogContainer } from "./styles";
import { cn } from "src/lib/cn";

type LedgerDisplayInfo = Exclude<ReturnType<typeof getLedgerDisplayInfo>, false>;

type LedgerEntryRecord = {
  id: string;
  amount: number;
  source?: string | null;
  context?: Record<string, unknown>;
  [key: string]: any;
};

type LedgerDisplayInfoWithAmount = LedgerDisplayInfo & { rawAmount: number };

type EntryItem = LedgerDisplayInfo & {
  type: "entry";
  rawAmount: number;
};

type ChatGroupItem = LedgerDisplayInfo & {
  type: "chat-group";
  groupKind?: "persistentChat" | "agenticChat";
  entries: LedgerDisplayInfo[];
  expandable: boolean;
  id: string;
  rawAmount: number;
  defaultExpanded: boolean;
};

type ProcessedLedgerEntry = EntryItem | ChatGroupItem;

type SubscriptionLike = {
  fetchRecentActivity: () => Promise<{
    success: boolean;
    entries: LedgerEntryRecord[];
  }>;
  admin?: boolean;
  data?: {
    admin?: boolean;
  };
};

type RecentActivityLogProps = {
  subscription: SubscriptionLike;
  onClose: () => void;
  showCreditsUsedToast: boolean;
  setShowCreditsUsedToast: (value: boolean) => void;
};

const LedgerEntry = ({ entry, isSubEntry, isAdmin }: { entry: LedgerDisplayInfo, isSubEntry?: boolean, isAdmin: boolean }) => {
  const hasInvocationId = entry.invocationId;
  const { setToast } = useToast();
  const clickTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const copyInvocationId = () => {
    if (!hasInvocationId) {
      return;
    }

    const canCopy = Boolean(navigator && navigator.clipboard);

    if (canCopy) {
      navigator.clipboard.writeText(entry.invocationId);
      setToast("ID copied to your clipboard");
    }
  };

  const openInvocationInAdmin = () => {
    if (!hasInvocationId || !isAdmin || !window?.location?.origin) {
      return;
    }

    const adminInvocationUrl = `${window.location.origin}/admin/invocation/${entry.invocationId}`;
    window.open(adminInvocationUrl, "_blank", "noopener,noreferrer");
    setToast("Invocation opened in a new tab");
  };

  const handleClick = () => {
    if (!isAdmin || !hasInvocationId) {
      return;
    }

    if (clickTimeoutRef.current) {
      clearTimeout(clickTimeoutRef.current);
    }

    // Delay single-click action so a fast double-click can cancel it.
    clickTimeoutRef.current = setTimeout(() => {
      openInvocationInAdmin();
      clickTimeoutRef.current = null;
    }, 220);
  };

  const handleDoubleClick = () => {
    if (clickTimeoutRef.current) {
      clearTimeout(clickTimeoutRef.current);
      clickTimeoutRef.current = null;
    }
    copyInvocationId();
  };

  return (
    <StyledEntryContainer className="entry">
      <div className="entry">
        <div
          onClick={handleClick}
          onDoubleClick={handleDoubleClick}
          className="entry-wrapper"
          style={{ cursor: hasInvocationId ? "pointer" : "auto" }}
        >
          <div className="entry-title">
            <div className="entry-label-with-toggle">
              <span
                aria-hidden="true"
                className="chevron-placeholder"
              />
              <div>
                <div className={cn("main-label", isSubEntry && "text-app-grey-1")}>{entry.largeText}</div>
                {entry.smallText && <div className="sub-label">{entry.smallText}</div>}
              </div>
            </div>
          </div>
          <div className="entry-amount">{entry.amount}</div>
        </div>
      </div>
    </StyledEntryContainer>
  );
};

const ChatGroupLedgerEntry = ({ group, isAdmin }: { group: ChatGroupItem, isAdmin: boolean }) => {
  const isExpandable = group.expandable;
  const [expanded, setExpanded] = useState<boolean>(isExpandable && group.defaultExpanded);
  const contentId = `${group.id}-items`;
  const isAgentic = group.groupKind === "agenticChat";
  const toggle = () => {
    if (!isExpandable) {
      return;
    }
    setExpanded((prev) => !prev);
  };

  return (
    <>
      <StyledEntryContainer className="entry chat-group pl-5">
        <div className="entry">
          <div className="entry-wrapper">
            <div className="entry-title">
              <div className="entry-label-with-toggle flex gap-2 items-center">
                {isExpandable ? (
                  <div
                    className={`chat-group-toggle ${isAgentic ? "agentic" : ""} ${expanded ? "expanded" : ""} w-[18px] h-[18px] p-0 min-w-0 mr-0 rounded-sm !bg-transparent`}
                    onClick={toggle}
                    aria-expanded={expanded}
                    aria-controls={contentId}
                    aria-label={expanded ? "Collapse chat activity" : "Expand chat activity"}
                  >
                    <InlineSVG
                      src="/assets/down-chevron.svg"
                      className="chevron-icon h-3 w-3"
                    />
                  </div>
                ) : (
                  <span
                    aria-hidden="true"
                    className="chevron-placeholder"
                  />
                )}
                <div className="flex items-center gap-1.5">
                  <div className="main-label">{group.largeText}</div>
                  {isAgentic && group.entries.length > 0 && (
                    <WithReset boxOnly>
                      <span
                        className="inline-flex items-center justify-center rounded-full text-[12px] leading-none font-medium min-w-[20px] h-[20px] px-1.5"
                        style={{ backgroundColor: "var(--grey-3)", color: "var(--grey-1)" }}
                      >
                        {group.entries.length}
                      </span>
                    </WithReset>
                  )}
                  {group.smallText && <div className="sub-label">{group.smallText}</div>}
                </div>
              </div>
            </div>
            <div className="entry-amount">
              {group.amount}
            </div>
          </div>
        </div>
      </StyledEntryContainer>
      {isExpandable && expanded && (
        <div
          className="chat-group-children pl-5"
          id={contentId}
        >
          {group.entries.map((entry: LedgerEntryRecord) => (
            <LedgerEntry
              isSubEntry={true}
              isAdmin={isAdmin}
              entry={entry}
              key={entry.ledgerEntryId}
            />
          ))}
        </div>
      )}
    </>
  );
};

const isAgenticChatSource = (source: string | null | undefined): boolean => {
  return source === "agenticChat/message"
    || source === "agentic-chat"
    || source === "agenticChat/plan/approve"
    || source === "agenticChat/plan/resume"
    || source === "agenticChat/plan/duplicate";
};


const buildLedgerEntries = (entries: LedgerEntryRecord[]): ProcessedLedgerEntry[] => {
  const processedEntries: ProcessedLedgerEntry[] = [];
  let currentChatGroup: { entries: LedgerEntryRecord[] } | null = null;
  let currentAgenticGroup: { entries: LedgerEntryRecord[] } | null = null;
  let chatGroupCounter = 0;
  let agenticGroupCounter = 0;
  let pendingZeroChat: LedgerDisplayInfo | null = null;

  const finalizeChatGroup = () => {
    if (!currentChatGroup) {
      return;
    }

    const childEntriesWithAmount = currentChatGroup.entries
      .map((entry) => {
        const info = getLedgerDisplayInfo(entry);
        if (!info) {
          return null;
        }
        return { ...info, rawAmount: entry.amount } as LedgerDisplayInfoWithAmount;
      })
      .filter((entry): entry is LedgerDisplayInfoWithAmount => Boolean(entry));

    if (childEntriesWithAmount.length) {
      const totalAmount = currentChatGroup.entries.reduce((sum, entry) => sum + entry.amount, 0);
      const aggregatedEntry = { ...currentChatGroup.entries[0], amount: totalAmount, source: "persistentChat" };
      const info = getLedgerDisplayInfo(aggregatedEntry);
      const hasConsumption = totalAmount !== 0;
      const hasMultipleEntries = childEntriesWithAmount.length > 1;
      const displayChildEntries: LedgerDisplayInfo[] = childEntriesWithAmount.map(({ rawAmount, ...rest }) => rest);

      if (info && hasConsumption && hasMultipleEntries) {
        const groupId = `chat-group-${chatGroupCounter++}`;
        processedEntries.push({
          type: "chat-group",
          groupKind: "persistentChat",
          ...info,
          largeText: `${info.largeText} (${childEntriesWithAmount.length})`,
          entries: displayChildEntries,
          expandable: true,
          id: groupId,
          rawAmount: totalAmount,
          defaultExpanded: false,
        });
      } else if (info && !hasConsumption) {
        pendingZeroChat = {
          ...info,
          largeText: "Chat",
          amount: info.amount,
        };
      } else {
        childEntriesWithAmount.forEach((entry) => {
          const { rawAmount, ...rest } = entry;
          processedEntries.push({
            ...rest,
            type: "entry",
            rawAmount,
          });
        });
      }
    }

    currentChatGroup = null;
  };

  const finalizeAgenticGroup = () => {
    if (!currentAgenticGroup) {
      return;
    }

    const childEntriesWithAmount = currentAgenticGroup.entries
      .map((entry) => {
        const info = getLedgerDisplayInfo(entry);
        if (!info) {
          return null;
        }
        return { ...info, rawAmount: entry.amount } as LedgerDisplayInfoWithAmount;
      })
      .filter((entry): entry is LedgerDisplayInfoWithAmount => Boolean(entry));

    if (childEntriesWithAmount.length) {
      const totalAmount = currentAgenticGroup.entries.reduce((sum, entry) => sum + entry.amount, 0);
      const displayChildEntries: LedgerDisplayInfo[] = childEntriesWithAmount.map(({ rawAmount, ...rest }) => rest);

      const aggregatedEntry = { ...currentAgenticGroup.entries[0], amount: totalAmount, source: "agenticChat/message" };
      const info = getLedgerDisplayInfo(aggregatedEntry);
      if (info) {
        const groupId = `agentic-group-${agenticGroupCounter++}`;
        processedEntries.push({
          type: "chat-group",
          groupKind: "agenticChat",
          ...info,
          largeText: "Chat Messages",
          entries: displayChildEntries,
          expandable: true,
          id: groupId,
          rawAmount: totalAmount,
          defaultExpanded: false,
        });
      }
    }

    currentAgenticGroup = null;
  };

  entries.forEach((entry) => {
    if (entry.source?.includes("persistentChat")) {
      finalizeAgenticGroup();
      if (!currentChatGroup) {
        currentChatGroup = { entries: [] };
      }
      currentChatGroup.entries.push(entry);
      return;
    }

    if (isAgenticChatSource(entry.source)) {
      finalizeChatGroup();
      if (pendingZeroChat) {
        processedEntries.push({ ...pendingZeroChat, type: "entry", rawAmount: 0 });
        pendingZeroChat = null;
      }
      // Story-bible edits should appear as standalone rows (like CTA entries),
      // not nested under the generic Chat Messages accordion.
      const storyBibleLabel = getStoryBibleLedgerContextLabel(entry);
      const isChatMessageEntry = entry.source === "agenticChat/message" || entry.source === "agentic-chat";
      if (storyBibleLabel && !isChatMessageEntry) {
        finalizeAgenticGroup();
        const info = getLedgerDisplayInfo(entry);
        if (!info) return;
        processedEntries.push({
          type: "entry",
          ...info,
          largeText: storyBibleLabel,
          rawAmount: entry.amount,
        });
        return;
      }
      if (!currentAgenticGroup) {
        currentAgenticGroup = { entries: [] };
      }
      currentAgenticGroup.entries.push(entry);
      return;
    }

    // Check if this entry would be hidden - if so, skip it without finalizing groups
    const info = getLedgerDisplayInfo(entry);
    if (!info) {
      return;
    }

    finalizeChatGroup();
    finalizeAgenticGroup();
    if (pendingZeroChat) {
      processedEntries.push({
        ...pendingZeroChat,
        type: "entry",
        rawAmount: 0,
      });
      pendingZeroChat = null;
    }
    processedEntries.push({ type: "entry", ...info, rawAmount: entry.amount });
  });

  finalizeChatGroup();
  finalizeAgenticGroup();
  if (pendingZeroChat) {
    processedEntries.push({
      ...pendingZeroChat,
      type: "entry",
      rawAmount: 0,
    });
  }

  return processedEntries;
};

const RecentActivityLog = ({ subscription, onClose, showCreditsUsedToast, setShowCreditsUsedToast }: RecentActivityLogProps) => {
  const [ledgerEntries, setLedgerEntries] = useState<ProcessedLedgerEntry[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const { can } = useAdminAccess();
  const isAdmin = can("invocations.readContent");

  useEffect(() => {
    const loadLedger = async () => {
      setLoading(true);
      const result = await subscription.fetchRecentActivity();
      if (result.success) {
        // only display the most recent entries in reverse chronological order
        let entries = result.entries;
        entries = mergeRambleEntries(entries);
        entries = mergeEntityImageBatchEntries(entries);
        entries = buildLedgerEntries(entries);

        setLedgerEntries(entries);
        setLoading(false);
      } else {
        setError(true);
        setLoading(false);
      }
    };

    loadLedger();
  }, []);

  // only show the most recent entries
  const entriesToShow = ledgerEntries.slice(0, 20);

  return (
    <StyledLogContainer>
      <div className="header">
        <div className="title">
          <h3>Recent Activity</h3>
        </div>
      </div>
      <div className='max-h-[320px] min-h-[235px] w-full mb-3 scrollbar-none'>

      <div className="log overflow-auto scrollbar-none mb-4 w-full pr-3">
        {loading && <div>Loading...</div>}
        {error && <div>There was a problem loading your most recent entries</div>}
        {entriesToShow.map((entry) =>
          entry.type === "chat-group" ? (
            <ChatGroupLedgerEntry
            isAdmin={isAdmin}
            group={entry}
            key={entry.id}
            />
          ) : (
            <LedgerEntry
            isAdmin={isAdmin}
            entry={entry}
            key={entry.ledgerEntryId}
            />
          )
        )}
      </div>
        </div>
      <div className="toggle-setting">
        <label className="switch">
          <input
            aria-label="Show or don't show credits just used toast"
            type="checkbox"
            id="creditsUsedToast"
            checked={showCreditsUsedToast}
            onChange={(e) => setShowCreditsUsedToast(e.target.checked)}
          />
          <span className="slider round"></span>
        </label>
        <label htmlFor="creditsUsedToast">Show credits pop-up</label>
      </div>
    </StyledLogContainer>
  );
};

export default RecentActivityLog;
